// ENG-2583 snap-to coverage. Pure, DOM-free geometry for (a) deciding which
// shape a connection click lands on and (b) snapping a drawn line's endpoint onto
// that shape. Supports all three node shapes a connection can attach to: a box
// (bbox), a polygon (traced vertices), and a line (LINESTRING points). Kept here
// so both the hit-test (PnidRenderer) and the endpoint snap (EditorWrapper) share
// one implementation and it stays unit-testable.

export interface Pt {
  x: number
  y: number
}

/** Nearest point on segment a→b to p. */
export function closestPointOnSegment(p: Pt, a: Pt, b: Pt): Pt {
  const dx = b.x - a.x
  const dy = b.y - a.y
  const lenSq = dx * dx + dy * dy
  if (lenSq === 0) return { x: a.x, y: a.y }
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq
  t = Math.max(0, Math.min(1, t))
  return { x: a.x + t * dx, y: a.y + t * dy }
}

/**
 * Nearest point on a path to `p`, with its distance. `closed` treats the points
 * as a polygon ring (wraps last→first); open treats them as a polyline.
 */
export function nearestPointOnPath(
  p: Pt,
  pts: Pt[],
  closed: boolean,
): { point: Pt; dist: number } {
  if (pts.length === 0) return { point: { ...p }, dist: Infinity }
  if (pts.length === 1) return { point: { ...pts[0] }, dist: Math.hypot(p.x - pts[0].x, p.y - pts[0].y) }
  let best: Pt = pts[0]
  let bestDist = Infinity
  const segments = closed ? pts.length : pts.length - 1
  for (let i = 0; i < segments; i++) {
    const a = pts[i]
    const b = pts[(i + 1) % pts.length]
    const c = closestPointOnSegment(p, a, b)
    const d = Math.hypot(p.x - c.x, p.y - c.y)
    if (d < bestDist) {
      bestDist = d
      best = c
    }
  }
  return { point: best, dist: bestDist }
}

/** Ray-casting test: is `p` inside the polygon `verts` (assumed a closed ring)? */
export function pointInPolygon(p: Pt, verts: Pt[]): boolean {
  if (verts.length < 3) return false
  let inside = false
  for (let i = 0, j = verts.length - 1; i < verts.length; j = i++) {
    const vi = verts[i]
    const vj = verts[j]
    const intersects =
      vi.y > p.y !== vj.y > p.y &&
      p.x < ((vj.x - vi.x) * (p.y - vi.y)) / (vj.y - vi.y) + vi.x
    if (intersects) inside = !inside
  }
  return inside
}

/**
 * Decide which endpoint of a drawn line (if any) a placed junction should snap to +
 * link. Returns the index of the endpoint within `catchRadius` of `center` — `0` or
 * the last index — preferring the NEARER one, or `null` if neither is in range.
 *
 * Crucially it never returns "both": snapping both ends of a short stub onto the same
 * junction center would collapse the line to a zero-length LINESTRING. The caller
 * snaps only the returned endpoint.
 */
export function junctionAttachEndpoint(pts: Pt[], center: Pt, catchRadius: number): number | null {
  if (pts.length < 2) return null
  const last = pts.length - 1
  const r2 = catchRadius * catchRadius
  const d0 = (pts[0].x - center.x) ** 2 + (pts[0].y - center.y) ** 2
  const dN = (pts[last].x - center.x) ** 2 + (pts[last].y - center.y) ** 2
  const in0 = d0 <= r2
  const inN = dN <= r2
  if (!in0 && !inN) return null
  return in0 && (!inN || d0 <= dN) ? 0 : last
}
